import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Hours / Days toggle shared across the Estimation accuracy tab. The trend
 * chart, period summary, and tasks table all read this so the user only ever
 * has to flip the switch once. Choice is persisted in localStorage.
 */
export type EstimationUnit = 'hours' | 'days';

/** Working day length used to translate hours into days. Matches
 *  `HOURS_PER_DAY` in `src/features/releases/SprintPlan.tsx`. */
export const ESTIMATION_HOURS_PER_DAY = 8;

const STORAGE_KEY = 'analytics.estimation.unit.v1';

function readStoredUnit(): EstimationUnit {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'days' || v === 'hours') return v;
  } catch {
    /* localStorage may be disabled in some browsers / private mode — ignore. */
  }
  return 'hours';
}

interface EstimationUnitContextValue {
  unit: EstimationUnit;
  setUnit: (unit: EstimationUnit) => void;
  hoursPerDay: number;
  /** Convert an hours value into the active unit. Returns the raw number so
   *  callers can format with their own decimal/sign rules. */
  toUnitValue: (hours: number) => number;
  /** Short suffix for inline use (`"h"` or `"d"`). */
  suffix: 'h' | 'd';
  /** Parenthesised form for table headers (`"(h)"` or `"(d)"`). */
  parenSuffix: '(h)' | '(d)';
}

const EstimationUnitContext = createContext<EstimationUnitContextValue | null>(null);

export const EstimationUnitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unit, setUnitState] = useState<EstimationUnit>(() => readStoredUnit());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, unit);
    } catch {
      /* ignore quota / disabled storage */
    }
  }, [unit]);

  const setUnit = useCallback((next: EstimationUnit) => setUnitState(next), []);

  const value = useMemo<EstimationUnitContextValue>(
    () => ({
      unit,
      setUnit,
      hoursPerDay: ESTIMATION_HOURS_PER_DAY,
      toUnitValue: (hours: number) => (unit === 'days' ? hours / ESTIMATION_HOURS_PER_DAY : hours),
      suffix: unit === 'days' ? 'd' : 'h',
      parenSuffix: unit === 'days' ? '(d)' : '(h)',
    }),
    [unit, setUnit]
  );

  return <EstimationUnitContext.Provider value={value}>{children}</EstimationUnitContext.Provider>;
};

/**
 * Falls back to a no-op "hours" implementation when used outside the
 * provider, so individual cards keep rendering instead of crashing if a
 * future caller forgets the wrapper.
 */
export function useEstimationUnit(): EstimationUnitContextValue {
  const ctx = useContext(EstimationUnitContext);
  if (ctx) return ctx;
  return {
    unit: 'hours',
    setUnit: () => {},
    hoursPerDay: ESTIMATION_HOURS_PER_DAY,
    toUnitValue: (h) => h,
    suffix: 'h',
    parenSuffix: '(h)',
  };
}
